import { useState, useEffect } from "react";
import { Plus, Users, Pencil, Trash2, Search, Building2, User, Landmark, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import CityAutocomplete from "../components/CityAutocomplete";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lista province italiane
const PROVINCE_ITALIANE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", 
  "BR", "BS", "BT", "BZ", "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ",
  "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE",
  "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO", "NU",
  "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA",
  "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV",
  "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

// Configurazione tipi cliente
const CUSTOMER_TYPES = {
  B2C: {
    label: "Privato",
    icon: User,
    color: "text-blue-400",
    bgColor: "bg-blue-400/10",
    borderColor: "border-blue-400/30",
    description: "Cliente privato - Codice Fiscale obbligatorio"
  },
  B2B: {
    label: "Azienda",
    icon: Building2,
    color: "text-green-400",
    bgColor: "bg-green-400/10",
    borderColor: "border-green-400/30",
    description: "Azienda privata - Partita IVA obbligatoria"
  },
  PA: {
    label: "Pubblica Amministrazione",
    icon: Landmark,
    color: "text-amber-400",
    bgColor: "bg-amber-400/10",
    borderColor: "border-amber-400/30",
    description: "PA - Codice IPA e CIG obbligatori"
  }
};

export default function Customers({ user }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    customer_type: "B2C",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    zip_code: "",
    fiscal_code: "",
    vat_number: "",
    sdi_code: "",
    pec: "",
    cig: "",
    cup: "",
    split_payment: false,
    notes: ""
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await authFetch(`${API}/customers`, { });
      if (response.ok) {
        setCustomers(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazioni client-side
    const errors = [];
    if (formData.customer_type === "B2C" && !formData.fiscal_code) {
      errors.push("Codice fiscale obbligatorio per cliente privato");
    }
    if (formData.customer_type === "B2B" && !formData.vat_number) {
      errors.push("Partita IVA obbligatoria per azienda");
    }
    if (formData.customer_type === "PA") {
      if (!formData.vat_number) errors.push("Partita IVA obbligatoria per PA");
      if (!formData.sdi_code || formData.sdi_code.length !== 6) {
        errors.push("Codice IPA (6 caratteri) obbligatorio per PA");
      }
      if (!formData.cig) errors.push("CIG obbligatorio per PA");
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }
    
    try {
      const url = editingCustomer 
        ? `${API}/customers/${editingCustomer.customer_id}`
        : `${API}/customers`;
      
      const response = await authFetch(url, {
        method: editingCustomer ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCustomer ? "Cliente aggiornato" : "Cliente creato");
        setShowDialog(false);
        resetForm();
        fetchCustomers();
      } else {
        const data = await response.json();
        throw new Error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error(error.message || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;
    
    try {
      const response = await authFetch(`${API}/customers/${customerId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Cliente eliminato");
        fetchCustomers();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_type: customer.customer_type || "B2C",
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      city: customer.city || "",
      province: customer.province || "",
      zip_code: customer.zip_code || "",
      fiscal_code: customer.fiscal_code,
      vat_number: customer.vat_number || "",
      sdi_code: customer.sdi_code || "",
      pec: customer.pec || "",
      cig: customer.cig || "",
      cup: customer.cup || "",
      split_payment: customer.split_payment || false,
      notes: customer.notes || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      customer_type: "B2C",
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      province: "",
      zip_code: "",
      fiscal_code: "",
      vat_number: "",
      sdi_code: "",
      pec: "",
      cig: "",
      cup: "",
      split_payment: false,
      notes: ""
    });
    setEditingCustomer(null);
    setTouched({});
  };

  // ─── Real-time Validation ──────────────────────────────────────
  const validateField = (field, value) => {
    const t = formData.customer_type;
    switch (field) {
      case "name":
        return value.trim().length >= 2 ? null : "Minimo 2 caratteri";
      case "fiscal_code":
        if (t === "B2C" && !value) return "Obbligatorio per privato";
        if (value && !/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(value) && value.length !== 16)
          return "Formato: 16 caratteri alfanumerici";
        return null;
      case "vat_number":
        if ((t === "B2B" || t === "PA") && !value) return "Obbligatorio";
        if (value && (!/^\d+$/.test(value) || value.length !== 11))
          return "11 cifre numeriche";
        return null;
      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Email non valida";
        return null;
      case "pec":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "PEC non valida";
        if (t === "B2B" && (!formData.sdi_code || formData.sdi_code === "0000000") && !value)
          return "Obbligatoria senza SDI";
        return null;
      case "zip_code":
        if (value && (!/^\d{5}$/.test(value))) return "5 cifre";
        return null;
      case "sdi_code":
        if (t === "PA" && (!value || value.length !== 6)) return "6 caratteri per PA";
        if (t === "B2B" && value && value !== "0000000" && value.length !== 7) return "7 caratteri o 0000000";
        return null;
      case "cig":
        if (t === "PA" && !value) return "Obbligatorio per PA";
        if (value && value.length < 10) return "10 caratteri";
        return null;
      case "address":
        return value.trim().length >= 3 ? null : "Minimo 3 caratteri";
      default:
        return null;
    }
  };

  const getFieldError = (field) => {
    if (!touched[field]) return null;
    return validateField(field, formData[field]);
  };

  const markTouched = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const fieldOk = (field) => touched[field] && !validateField(field, formData[field]) && formData[field];

  const FieldStatus = ({ field }) => {
    const err = getFieldError(field);
    if (!touched[field]) return null;
    if (err) return <p className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid={`error-${field}`}><AlertCircle className="w-3 h-3" />{err}</p>;
    if (formData[field]) return <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />;
    return null;
  };

  const inputClass = (field) => {
    const err = getFieldError(field);
    if (!touched[field]) return "form-input";
    if (err) return "form-input border-red-500/50 focus:border-red-500";
    if (formData[field]) return "form-input border-emerald-500/30 focus:border-emerald-500";
    return "form-input";
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      customer_type: type,
      // Reset SDI per PA (6 char) vs B2B (7 char)
      sdi_code: type === "PA" ? prev.sdi_code.slice(0, 6) : prev.sdi_code,
      // Split payment automatico per PA
      split_payment: type === "PA" ? true : prev.split_payment
    }));
  };

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.fiscal_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.vat_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || c.customer_type === filterType;
    return matchesSearch && matchesType;
  });

  const getCustomerTypeInfo = (type) => CUSTOMER_TYPES[type] || CUSTOMER_TYPES.B2C;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Anagrafica Clienti</h1>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="new-customer-btn" className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                {editingCustomer ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 mt-4 pr-2">
              
              {/* TIPO CLIENTE - Selezione con cards */}
              <div className="space-y-2">
                <label className="form-label">Tipo Cliente *</label>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(CUSTOMER_TYPES).map(([type, config]) => {
                    const Icon = config.icon;
                    const isSelected = formData.customer_type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeChange(type)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? `${config.borderColor} ${config.bgColor}` 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-1 ${isSelected ? config.color : 'text-zinc-500'}`} />
                        <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-zinc-400'}`}>
                          {config.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {getCustomerTypeInfo(formData.customer_type).description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group col-span-2">
                  <label className="form-label">
                    {formData.customer_type === "B2C" ? "Nome e Cognome" : "Ragione Sociale"} *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className={inputClass("name")}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      onBlur={() => markTouched("name")}
                      placeholder={formData.customer_type === "B2C" ? "Mario Rossi" : "Azienda SRL"}
                      required
                      data-testid="customer-name-input"
                    />
                    {fieldOk("name") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                  </div>
                  <FieldStatus field="name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      className={inputClass("email")}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      onBlur={() => markTouched("email")}
                      data-testid="customer-email-input"
                    />
                    {fieldOk("email") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                  </div>
                  <FieldStatus field="email" />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                
                {/* Indirizzo completo per fatturazione */}
                <div className="form-group col-span-2">
                  <label className="form-label">Indirizzo (Via/Piazza e numero) *</label>
                  <div className="relative">
                    <input
                      type="text"
                      className={inputClass("address")}
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      onBlur={() => markTouched("address")}
                      placeholder="Es: Via Roma 123"
                      required
                      data-testid="customer-address-input"
                    />
                    {fieldOk("address") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                  </div>
                  <FieldStatus field="address" />
                </div>
                <div className="form-group">
                  <label className="form-label">Città *</label>
                  <CityAutocomplete
                    value={formData.city}
                    onChange={(city) => setFormData({...formData, city: city})}
                    onProvinceChange={(province) => setFormData(prev => ({...prev, province: province}))}
                    onCapChange={(cap) => setFormData(prev => ({...prev, zip_code: cap}))}
                    placeholder="Cerca città..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Provincia *</label>
                  <select
                    className="form-select"
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                    required
                  >
                    <option value="">Seleziona...</option>
                    {PROVINCE_ITALIANE.map(prov => (
                      <option key={prov} value={prov}>{prov}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">CAP *</label>
                  <div className="relative">
                    <input
                      type="text"
                      className={inputClass("zip_code")}
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5)})}
                      onBlur={() => markTouched("zip_code")}
                      placeholder="Es: 20100"
                      maxLength={5}
                      required
                      data-testid="customer-zip-input"
                    />
                    {fieldOk("zip_code") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                  </div>
                  <FieldStatus field="zip_code" />
                </div>
                
                {/* CODICE FISCALE - Obbligatorio per B2C */}
                <div className="form-group">
                  <label className="form-label">
                    Codice Fiscale {formData.customer_type === "B2C" && <span className="text-red-400">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className={`${inputClass("fiscal_code")} uppercase`}
                      value={formData.fiscal_code}
                      onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                      onBlur={() => markTouched("fiscal_code")}
                      maxLength={16}
                      required={formData.customer_type === "B2C"}
                      placeholder="RSSMRA80A01H501U"
                      data-testid="customer-cf-input"
                    />
                    {fieldOk("fiscal_code") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                  </div>
                  <FieldStatus field="fiscal_code" />
                </div>
                
                {/* Sezione Dati Fiscali - Visibile per B2B e PA */}
                {(formData.customer_type === "B2B" || formData.customer_type === "PA") && (
                  <>
                    <div className="col-span-2 border-t border-zinc-700 pt-4 mt-2">
                      <h4 className="text-sm font-medium text-primary mb-3">
                        Dati Fatturazione Elettronica
                      </h4>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Partita IVA *</label>
                      <div className="relative">
                        <input
                          type="text"
                          className={`${inputClass("vat_number")} uppercase`}
                          value={formData.vat_number}
                          onChange={(e) => setFormData({...formData, vat_number: e.target.value.replace(/\D/g, '').slice(0, 11)})}
                          onBlur={() => markTouched("vat_number")}
                          placeholder="12345678901"
                          maxLength={11}
                          required
                          data-testid="customer-piva-input"
                        />
                        {fieldOk("vat_number") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                      </div>
                      <FieldStatus field="vat_number" />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        {formData.customer_type === "PA" ? "Codice IPA (6 car.) *" : "Codice SDI (7 car.)"}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className={`${inputClass("sdi_code")} uppercase`}
                          value={formData.sdi_code}
                          onChange={(e) => setFormData({...formData, sdi_code: e.target.value.toUpperCase().slice(0, formData.customer_type === "PA" ? 6 : 7)})}
                          onBlur={() => markTouched("sdi_code")}
                          placeholder={formData.customer_type === "PA" ? "UFXXXX" : "0000000"}
                          maxLength={formData.customer_type === "PA" ? 6 : 7}
                          required={formData.customer_type === "PA"}
                          data-testid="customer-sdi-input"
                        />
                        {fieldOk("sdi_code") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                      </div>
                      <FieldStatus field="sdi_code" />
                    </div>
                    
                    {formData.customer_type === "B2B" && (
                      <div className="form-group col-span-2">
                        <label className="form-label">
                          PEC {(!formData.sdi_code || formData.sdi_code === "0000000") && <span className="text-red-400">*</span>}
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            className={inputClass("pec")}
                            value={formData.pec}
                            onChange={(e) => setFormData({...formData, pec: e.target.value})}
                            onBlur={() => markTouched("pec")}
                            placeholder="esempio@pec.it"
                            required={!formData.sdi_code || formData.sdi_code === "0000000"}
                            data-testid="customer-pec-input"
                          />
                          {fieldOk("pec") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                        </div>
                        <FieldStatus field="pec" />
                        <p className="text-xs text-zinc-500 mt-1">
                          Obbligatoria se codice SDI è 0000000 o vuoto
                        </p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Sezione PA - CIG/CUP */}
                {formData.customer_type === "PA" && (
                  <>
                    <div className="col-span-2 border-t border-zinc-700 pt-4 mt-2">
                      <h4 className="text-sm font-medium text-amber-400 mb-3">
                        Dati Specifici Pubblica Amministrazione
                      </h4>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">CIG (Codice Identificativo Gara) *</label>
                      <div className="relative">
                        <input
                          type="text"
                          className={`${inputClass("cig")} uppercase`}
                          value={formData.cig}
                          onChange={(e) => setFormData({...formData, cig: e.target.value.toUpperCase()})}
                          onBlur={() => markTouched("cig")}
                          placeholder="Z1234567AB"
                          maxLength={10}
                          required
                          data-testid="customer-cig-input"
                        />
                        {fieldOk("cig") && <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                      </div>
                      <FieldStatus field="cig" />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">CUP (Codice Unico Progetto)</label>
                      <input
                        type="text"
                        className="form-input uppercase"
                        value={formData.cup}
                        onChange={(e) => setFormData({...formData, cup: e.target.value.toUpperCase()})}
                        placeholder="J11B12000000001"
                        maxLength={15}
                      />
                    </div>
                    
                    <div className="form-group col-span-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.split_payment}
                          onChange={(e) => setFormData({...formData, split_payment: e.target.checked})}
                          className="form-checkbox"
                          disabled={formData.customer_type === "PA"}
                        />
                        <span className="text-sm text-zinc-300">Split Payment (IVA versata dalla PA)</span>
                      </label>
                      <p className="text-xs text-zinc-500 mt-1 ml-6">
                        Attivo di default per la Pubblica Amministrazione
                      </p>
                    </div>
                  </>
                )}
                
                <div className="form-group col-span-2">
                  <label className="form-label">Note</label>
                  <textarea
                    className="form-textarea h-20"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingCustomer ? "Salva Modifiche" : "Crea Cliente"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder="Cerca cliente..."
            className="form-input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button 
            variant={filterType === "" ? "default" : "ghost"}
            size="sm"
            onClick={() => setFilterType("")}
            className={filterType === "" ? "bg-primary text-black" : "text-zinc-400"}
          >
            Tutti
          </Button>
          {Object.entries(CUSTOMER_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            return (
              <Button
                key={type}
                variant={filterType === type ? "default" : "ghost"}
                size="sm"
                onClick={() => setFilterType(type)}
                className={filterType === type ? `${config.bgColor} ${config.color}` : "text-zinc-400"}
              >
                <Icon className="w-4 h-4 mr-1" />
                {config.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Customers List */}
      {filteredCustomers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Nome</th>
                <th>Email</th>
                <th>C.F. / P.IVA</th>
                <th>SDI/IPA</th>
                <th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => {
                const typeInfo = getCustomerTypeInfo(customer.customer_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <tr key={customer.customer_id}>
                    <td>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${typeInfo.bgColor}`}>
                        <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                        <span className={`text-xs font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className="font-medium text-white">{customer.name}</td>
                    <td>{customer.email}</td>
                    <td className="font-['JetBrains_Mono'] uppercase text-xs">
                      {customer.vat_number ? (
                        <span title="Partita IVA">P.IVA: {customer.vat_number}</span>
                      ) : (
                        <span title="Codice Fiscale">CF: {customer.fiscal_code}</span>
                      )}
                    </td>
                    <td className="font-['JetBrains_Mono'] uppercase text-xs">
                      {customer.sdi_code || "-"}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(customer)}
                          className="text-primary hover:text-primary/80"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer.customer_id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Users className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessun cliente</p>
          <p className="empty-state-text">
            {searchTerm || filterType ? "Nessun risultato trovato" : "Aggiungi il tuo primo cliente"}
          </p>
        </div>
      )}
    </div>
  );
}
